//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
// avatar imports
import { IGenericProps } from "./Avatar.d";
import * as s from "./Avatar.styles";
import { colors, greys } from "@clockwise/web-commons/src/styles/color.styles";
// other imports
import { LogoClock } from "@components/logo";
// material-ui
import { SvgIconProps } from "@material-ui/core/SvgIcon";
import { Cancel, CheckCircle } from "@material-ui/icons";

//////////////////
// COMPONENT
//////////////////
export const AvatarGeneric = ({
  backgroundColor,
  backgroundImage,
  onClick,
  children,
  size,
  style,
  isNonActionable,
  isUser,
  hideUserBadge,
  responseStatus,
}: IGenericProps) => {
  const dimensions = {
    width: size || "100%",
    height: size || "100%",
  };

  let background: React.CSSProperties["background"] = s.gradient;
  if (!!backgroundImage) {
    background = `center / contain no-repeat url(${backgroundImage})`;
  } else if (!!backgroundColor) {
    background = backgroundColor;
  }

  const styles = { ...s.avatar, ...dimensions, background, ...style };

  if (!!onClick) {
    return (
      <div
        style={{ ...styles, cursor: isNonActionable ? "unset" : "pointer" }}
        onClick={() => onClick()}
        cw-id="user-avatar"
        role="img"
        aria-label={`User avatar for ${children?.toLocaleString()}`}
      >
        {children}
        {maybeRenderUserBadge(isUser, size, hideUserBadge)}
        {maybeRenderResponseStatusBadge(size, responseStatus, hideUserBadge)}
      </div>
    );
  } else {
    return (
      <div style={styles}>
        {children}
        {maybeRenderUserBadge(isUser, size, hideUserBadge)}
        {maybeRenderResponseStatusBadge(size, responseStatus, hideUserBadge)}
      </div>
    );
  }
};

const maybeRenderUserBadge = (isUser?: boolean, size?: number, hideUserBadge?: boolean) => {
  if (!isUser || !!hideUserBadge) {
    return <span />;
  }

  const badgeStyle = {
    ...s.badge,
    width: size && size > 50 ? 22 : 18,
    height: size && size > 50 ? 22 : 18,
    right: size && size > 60 ? 0 : -3,
    bottom: size && size > 60 ? 0 : -3,
  };
  const logoSize = badgeStyle.width - 4;

  return (
    <div style={badgeStyle}>
      <LogoClock size={logoSize} fill={greys.white} />
    </div>
  );
};

const responseStatusIconColorMap: Record<
  ISchema.ResponseStatusEnum,
  { icon: React.ComponentType<SvgIconProps>; color: string } | null
> = {
  [ISchema.ResponseStatusEnum.Accepted]: {
    icon: CheckCircle,
    color: colors.green.standard,
  },
  [ISchema.ResponseStatusEnum.Tentative]: {
    icon: CheckCircle,
    color: colors.yellow.standard,
  },
  [ISchema.ResponseStatusEnum.Declined]: {
    icon: Cancel,
    color: colors.red.standard,
  },
  [ISchema.ResponseStatusEnum.NeedsAction]: null,
  [ISchema.ResponseStatusEnum.NotApplicable]: null,
};

const maybeRenderResponseStatusBadge = (
  size?: number,
  responseStatus?: ISchema.ResponseStatusEnum,
  hideUserBadge?: boolean,
) => {
  if (!responseStatus || !hideUserBadge) {
    return null;
  }

  const iconColor = responseStatusIconColorMap[responseStatus];

  if (!iconColor) {
    return null;
  }

  const { icon: Icon, color } = iconColor;

  const badgeStyle = {
    ...s.responseBadge,
    width: size && size > 50 ? 20 : 16,
    height: size && size > 50 ? 20 : 16,
    right: size && size > 60 ? 0 : -3,
    bottom: size && size > 60 ? 0 : -3,
  };

  const logoSize = badgeStyle.width;

  return (
    <div style={badgeStyle}>
      <Icon style={{ color, width: logoSize, height: logoSize }} />
    </div>
  );
};
